import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import { withDeepLink } from "components/common/useAppsflyerOneLink";
import trackEvent from "components/tracking/trackEvent";
import type { TrackLocation } from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import apple from "components/website/img/apple-appstore.svg";
import google from "components/website/img/playstore.svg";
import { isAndroid, isIOS } from "react-device-detect";
import TiktokPixel from "tiktok-pixel";
import useReactRouter from "use-react-router";
import { copyLinkForAppHandoff } from "utils/copyLinkForAppHandoff";
import "./AppStoreButtons.scss";

type AppStoreButtonsProps = {
  location?: TrackLocation;
  // OneLink deep-link payload (see withDeepLink): what the app opens after installing from these
  // badges. Only /join/:tripId passes it today.
  deepLink?: { value: string; sub1?: string };
};

const AppStoreButtons = ({ location = "marketing_site", deepLink }: AppStoreButtonsProps) => {
  const { match } = useReactRouter();
  // 123yb6937h3: both badges resolve to the same attributed OneLink -- no user-facing download
  // link may ever be a direct apps.apple.com/play.google.com URL, so there's no separate desktop
  // fallback to pick per badge anymore. See useAppDownloadUrl's doc comment in useAppStoreLink.ts.
  //
  // Known trade-off: OneLink's own desktop redirect can only point at one destination, and for
  // this template (cgUq) that's the App Store. So a desktop click on the Google Play badge lands
  // on the App Store page, not Google Play. Real regression, accepted deliberately over ever
  // emitting an unattributed direct link -- fix is an AppsFlyer dashboard change (a desktop
  // fallback URL on the template), not code.
  const oneLinkUrl = useAppDownloadUrl();
  const appDownloadUrl = deepLink ? withDeepLink(oneLinkUrl, deepLink.value, deepLink.sub1) : oneLinkUrl;

  // The invite page (/join/:tripId) is the only caller with a deep link today. On an iPhone its App Store
  // badge also copies the invite URL for the app to pick up after the install (AppsFlyer's free plan no
  // longer carries the deep link through the store). On Android its Google Play badge does the same, for
  // installs where Play hands the app `(not set)` instead of the OneLink's referrer (CU 123yb69552x).
  // Read isIOS / isAndroid at click time, never while rendering: the marketing bundle is server-rendered
  // and this component hydrates.
  const isInviteLink = !!deepLink?.value.startsWith("join/");

  const handleTracking = (type) => {
    trackEvent(type === "android" ? "click_googleplay_cta" : "click_appstore_cta", { path: match.path }, location);
    trackFathom(type === "android" ? "BNQKH0MH" : "FD21GVOP");
    TiktokPixel.track("Download", { description: type });
  };

  return (
    <div className="appstore-logos">
      <a
        href={appDownloadUrl}
        className="apple"
        aria-label="Download Stippl on the App Store"
        onClick={() => {
          // Synchronous and before the anchor navigates: the copy needs the click's user gesture. No
          // preventDefault, the badge still follows its href.
          if (isInviteLink && isIOS) copyLinkForAppHandoff(window.location.href);
          handleTracking("apple");
        }}
      >
        <img src={apple} width={127} height={38} alt="App Store" />
      </a>
      <a
        href={appDownloadUrl}
        className="google"
        aria-label="Get Stippl on Google Play"
        onClick={() => {
          if (isInviteLink && isAndroid) copyLinkForAppHandoff(window.location.href);
          handleTracking("android");
        }}
      >
        <img src={google} width={127} height={38} alt="Google Play" />
      </a>
    </div>
  );
};

export default AppStoreButtons;
